/**
 * Action Briefing (content-first redesign) — the what-moved digest for the
 * 法规×痛点×草案 analysis.
 *
 * Information choice (the founder's correction): the categorical axes are
 * near-uniform across topics (strict law everywhere; honest-mock verdicts all
 * "watch"), so rows DO NOT render category pills/bars. Each row leads with the
 * CONTENT that varies:
 *
 *   kicker  — 草案/提案 chip · instrument · [FR] country chip (TRIS) · deadline
 *             countdown (standstill/effective date) or fetched date
 *   headline— the latest draft's verbatim title (the actual news)
 *   context — topic name + counts (现行法 ×n · 草案 ×n · 痛点 ×n only if >0)
 *   teaser  — one verbatim pain quote when VOC exists
 *   action  — 「深度研判 ▸」fires the ONE expensive live brief for this topic
 *             (never batched); the diagnosis subtype chip replaces the button
 *             once cached for the session.
 *
 * Topics with no drafts AND no pain collapse into a per-domain 法规存量 footer
 * (which also absorbs the killed CrossAnalysisMatrix's rule-stock audit role).
 *
 * ADVISORY ONLY (ADR-0002): read-only view; advisory tag + disclaimer pinned;
 * every brief carries isVerdict:false; absence rendered as absence, never as a
 * fabricated value.
 */

import { useRef, useState } from "react";
import type { OpportunityBrief } from "@regpulse/contracts";

import { fetchOpportunityBrief, type OpportunityRadarEntry } from "../services/api";
import { useManualRadar } from "../lib/useManualRadar";
import {
  DIAGNOSIS_LABEL,
  byWhatMoved,
  daysUntil,
  hasContent,
  nearestDeadline,
  parseCountryTag,
  topicLabel,
} from "../lib/radar-view";
import {
  getCachedBrief,
  invalidateBriefCache,
  setCachedBrief,
} from "../lib/brief-cache";

const STRICTNESS_LABEL: Record<string, string> = {
  strict: "严格",
  moderate: "中等",
  light: "宽松",
  none: "无",
};
const DIRECTION_LABEL: Record<string, string> = {
  tightening: "收紧中",
  loosening: "放松中",
  unclear: "不明",
  new: "新规",
  none: "无草案",
};
const INTENSITY_LABEL: Record<string, string> = {
  high: "高",
  med: "中",
  low: "低",
  none: "无",
};
const OPP_TYPE_LABEL: Record<string, string> = {
  opportunity: "机会",
  risk: "风险",
  watch: "观察",
  none: "—",
};

function DeadlineChip({ r, nowIso }: { r: OpportunityRadarEntry; nowIso: string }) {
  const d = r.latestDraft;
  if (!d) return null;
  if (d.effectiveDate && d.effectiveDate >= nowIso.slice(0, 10)) {
    const days = daysUntil(d.effectiveDate, nowIso);
    return (
      <span className="ab-chip ab-chip--deadline" data-testid={`ab-deadline-${r.topicId}`}>
        截止 {d.effectiveDate} · 还有 {days} 天
      </span>
    );
  }
  return <span className="ab-chip">收录 {d.fetchedAt.slice(0, 10)}</span>;
}

export function ActionBriefing({ signalCount }: { signalCount: number }) {
  const { status, radar, disclaimer, error: radarError, ranAt, isStale, staleCount, run } =
    useManualRadar(signalCount);
  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);
  const [brief, setBrief] = useState<OpportunityBrief | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState<string | null>(null);
  const [openDomains, setOpenDomains] = useState<Set<string>>(new Set());
  const briefRequestId = useRef(0);
  // One clock per render keeps the countdown math pure within a pass.
  const nowIso = new Date().toISOString();

  const active = radar.filter(hasContent).sort(byWhatMoved(nowIso));
  const baseline = radar.filter((r) => !hasContent(r));
  const totalDrafts = radar.reduce((n, r) => n + (r.draftCount ?? 0), 0);
  const draftTopics = radar.filter((r) => (r.draftCount ?? 0) > 0).length;
  const totalPain = radar.reduce((n, r) => n + (r.intelCount ?? 0), 0);
  const painTopics = radar.filter((r) => (r.intelCount ?? 0) > 0).length;
  const deadline = nearestDeadline(radar, nowIso);

  // A new radar run invalidates expanded state AND the session brief cache —
  // every cached brief was computed from the previous signal set.
  function handleRun(): void {
    briefRequestId.current++;
    setExpandedTopicId(null);
    setBrief(null);
    setBriefError(null);
    setBriefLoading(false);
    invalidateBriefCache();
    run();
  }

  async function toggleRow(topicId: string): Promise<void> {
    if (expandedTopicId === topicId) {
      setExpandedTopicId(null);
      return;
    }
    const requestId = ++briefRequestId.current;
    setExpandedTopicId(topicId);
    setBriefError(null);
    const cached = getCachedBrief(topicId);
    if (cached) {
      setBrief(cached);
      setBriefLoading(false);
      return;
    }
    setBrief(null);
    setBriefLoading(true);
    try {
      const res = await fetchOpportunityBrief(topicId);
      if (requestId === briefRequestId.current) {
        setCachedBrief(topicId, res.brief);
        setBrief(res.brief);
      }
    } catch (e) {
      if (requestId === briefRequestId.current) {
        setBriefError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (requestId === briefRequestId.current) setBriefLoading(false);
    }
  }

  function renderRow(r: OpportunityRadarEntry) {
    const label = topicLabel(r.topicId);
    // 未归类 is a triage BASKET, not a topic: its signals are unrelated to each
    // other (vocabulary-gap VOC + deliberately-unmapped out-of-scope drafts), so
    // a live brief over it can only conclude "关联过弱" — render it as a basket
    // with reassignment guidance instead of burning a 30–45s paid analysis.
    if (r.topicId === "unclassified") {
      return (
        <li key={r.topicId} className="ab-row-wrap" id={`ab-row-anchor-${r.topicId}`}>
          <div className="ab-card ab-card--basket" data-testid="ab-basket">
            <span className="ab-card__kicker">
              <span className="ab-chip">待人工归类</span>
            </span>
            <span className="ab-card__headline">
              未归类信号篮 · 草案 ×{r.draftCount ?? 0} · 痛点 ×{r.intelCount ?? 0}
            </span>
            <span className="ab-card__context">
              <span className="ab-card__domain">
                这里的信号彼此无关（词表未命中的 VOC + 刻意不映射的超纲草案），不构成可研判的主题。
              </span>
            </span>
            {r.painSample && (
              <span className="ab-card__quote">
                “{r.painSample.quote.slice(0, 60)}
                {r.painSample.quote.length > 60 ? "…" : ""}” — 买家原话（待归类）
              </span>
            )}
            <span className="ab-card__action">
              <span className="ab-card__domain">
                处理：在 Discovery &amp; Review 抽屉对单条信号「重新归类」，或 Dismiss 后用「扫描本配置全部痛点」重采。
              </span>
            </span>
          </div>
        </li>
      );
    }
    const expanded = expandedTopicId === r.topicId;
    const cached = getCachedBrief(r.topicId);
    const diag = cached?.opportunity.diagnosis;
    const draft = r.latestDraft;
    const parsed = draft ? parseCountryTag(draft.title) : undefined;
    return (
      <li key={r.topicId} className="ab-row-wrap" id={`ab-row-anchor-${r.topicId}`}>
        <button
          type="button"
          data-testid={`ab-row-${r.topicId}`}
          className={`ab-card${expanded ? " ab-card--open" : ""}`}
          aria-expanded={expanded}
          onClick={() => void toggleRow(r.topicId)}
        >
          {/* kicker: what kind of motion + where + when */}
          <span className="ab-card__kicker">
            {draft && (
              <span className="ab-chip ab-chip--kind">
                {draft.changeKind === "proposed" ? "提案" : "草案"}
              </span>
            )}
            {draft && <span className="ab-card__instrument">{draft.instrument}</span>}
            {parsed?.country && (
              <span className="ab-chip ab-chip--country" data-testid={`ab-country-${r.topicId}`}>
                {parsed.country}
              </span>
            )}
            <DeadlineChip r={r} nowIso={nowIso} />
          </span>

          {/* headline: the actual news (or the pain, when only VOC exists) */}
          <span className="ab-card__headline" data-testid={`ab-headline-${r.topicId}`}>
            {parsed ? parsed.headline : `市场痛点信号 ×${r.intelCount ?? 0}（暂无草案）`}
          </span>

          {/* context: topic + differentiated counts (absence renders nothing) */}
          <span className="ab-card__context">
            <span className="ab-card__topic">{label.zh}</span>
            <span className="ab-card__domain">{r.domainId}</span>
            <span className="ab-chip">现行法 ×{r.ruleCount ?? 0}</span>
            {(r.draftCount ?? 0) > 0 && <span className="ab-chip">草案 ×{r.draftCount}</span>}
            {(r.intelCount ?? 0) > 0 && (
              <span className="ab-chip ab-chip--pain">痛点 ×{r.intelCount}</span>
            )}
          </span>

          {/* pain teaser: the only pre-expansion VOC surface */}
          {r.painSample && (
            <span className="ab-card__quote" data-testid={`ab-quote-${r.topicId}`}>
              “{r.painSample.quote.slice(0, 60)}
              {r.painSample.quote.length > 60 ? "…" : ""}” — 买家原话
            </span>
          )}

          {/* action: diagnosis chip once cached, else the explicit live action */}
          <span className="ab-card__action">
            {diag && diag !== "watch" && diag !== "none" ? (
              <span
                className={`ab-diag-chip ab-diag-chip--${cached!.opportunity.type}`}
                data-testid={`ab-diag-${r.topicId}`}
              >
                {DIAGNOSIS_LABEL[diag]?.zh.split(" · ")[0] ?? diag}
              </span>
            ) : cached ? (
              <span className="ab-diag-chip" data-testid={`ab-diag-${r.topicId}`}>
                {OPP_TYPE_LABEL[cached.opportunity.type]} · 已研判
              </span>
            ) : (
              <span className="ab-card__cta">深度研判 ▸（live · 约30–45秒）</span>
            )}
          </span>
        </button>

        {expanded && (
          <div className="ab-detail" data-testid={`ab-detail-${r.topicId}`}>
            {briefLoading && <p className="ab-detail__loading">分析中…（live 内容研判，约 30–45 秒）</p>}
            {briefError && (
              <p role="alert" className="ab-detail__error">
                分析失败：{briefError}
              </p>
            )}
            {brief && brief.topicId === r.topicId && (
              <article className="opp-brief">
                {getCachedBrief(r.topicId) === brief && ranAt && (
                  <p className="ab-detail__cachenote">已缓存 · 基于 {ranAt} 信号集</p>
                )}
                <div className="opp-axes">
                  <div data-testid="axis-current-law" className="opp-axis">
                    <h4>① 现行法 · {STRICTNESS_LABEL[brief.currentLaw.strictness]}</h4>
                    <p>{brief.currentLaw.summary}</p>
                    {brief.currentLaw.citations.length > 0 && (
                      <small className="ab-citations">{brief.currentLaw.citations.join(" · ")}</small>
                    )}
                  </div>
                  <div data-testid="axis-draft" className="opp-axis">
                    <h4>② 草案走向 · {DIRECTION_LABEL[brief.draftTrajectory.direction]}</h4>
                    <p>{brief.draftTrajectory.summary}</p>
                  </div>
                  <div data-testid="axis-intel" className="opp-axis">
                    <h4>③ 市场痛点 · 强度 {INTENSITY_LABEL[brief.intel.intensity]}</h4>
                    {brief.intel.painThemes.length > 0 ? (
                      <ul>
                        {brief.intel.painThemes.map((t, i) => (
                          <li key={`${t}-${i}`}>{t}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="opp-empty">暂无市场痛点信号</p>
                    )}
                  </div>
                </div>

                <div
                  data-testid="ab-thesis"
                  className={`opp-thesis opp-thesis--${brief.opportunity.type}`}
                >
                  <strong>
                    {OPP_TYPE_LABEL[brief.opportunity.type] ?? brief.opportunity.type} · 置信{" "}
                    {brief.opportunity.confidence}
                  </strong>
                  {brief.opportunity.diagnosis &&
                    brief.opportunity.diagnosis !== "watch" &&
                    brief.opportunity.diagnosis !== "none" && (
                      <p className="ab-diagnosis" data-testid="ab-diagnosis">
                        <span className="ab-diagnosis__label">
                          {DIAGNOSIS_LABEL[brief.opportunity.diagnosis]?.zh ??
                            brief.opportunity.diagnosis}
                        </span>
                        {DIAGNOSIS_LABEL[brief.opportunity.diagnosis]?.note && (
                          <span className="ab-diagnosis__note">
                            ⚠ {DIAGNOSIS_LABEL[brief.opportunity.diagnosis]!.note}
                          </span>
                        )}
                      </p>
                    )}
                  <p>{brief.opportunity.thesis.zh}</p>
                  <p lang="en" className="opp-thesis__en">
                    {brief.opportunity.thesis.en}
                  </p>
                  {brief.opportunity.reasoningTrace && (
                    <details>
                      <summary>推理依据</summary>
                      <p>{brief.opportunity.reasoningTrace}</p>
                    </details>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </li>
    );
  }

  // 法规存量 footer: per-domain rule-stock audit (absorbs the killed matrix).
  const domains = [...new Set(baseline.map((r) => r.domainId))].sort();

  return (
    <section data-testid="action-briefing" className="action-briefing">
      <header className="cross-matrix__head">
        <h2>行动简报 · 法规 × 痛点 × 草案</h2>
        <span className="advisory-tag" data-testid="ab-advisory-tag">
          advisory · 非合规结论
        </span>
      </header>

      <div className="analysis-controls">
        {status === "idle" ? (
          <>
            <button type="button" data-testid="ab-run" className="analysis-run-btn" onClick={handleRun}>
              ▶ 运行三轴分析
            </button>
            <span className="analysis-hint">
              先收集痛点（「扫描本配置全部痛点」）/ 载入 WP29 真实 feed，再运行。
            </span>
          </>
        ) : (
          <>
            <button
              type="button"
              data-testid="ab-rerun"
              className="analysis-run-btn"
              onClick={handleRun}
              disabled={status === "loading"}
            >
              {status === "loading" ? "分析中…" : "↻ 重新分析"}
            </button>
            {ranAt && <span className="analysis-ranat">上次分析 {ranAt}</span>}
          </>
        )}
      </div>

      {isStale && (
        <p role="status" data-testid="ab-stale" className="analysis-stale">
          有 {staleCount} 条信号变动入审查队列，建议重新分析以刷新结果。
        </p>
      )}
      {status === "loading" && (
        <p role="status" className="cross-matrix__loading">
          分析中…（扫描全部法规主题）
        </p>
      )}
      {status === "error" && (
        <p role="alert" className="cross-matrix__error">
          分析失败：{radarError}
        </p>
      )}

      {status === "ok" && (
        <>
          {/* Masthead: the finding in real numbers + real content. */}
          <p className="ab-lead" data-testid="ab-lead">
            {totalDrafts > 0 ? (
              <>
                <strong>{totalDrafts}</strong> 条草案动向，涉及 <strong>{draftTopics}</strong>/
                {radar.length} 个主题。
                {deadline && (
                  <>
                    {" "}
                    最近截止：<strong>{topicLabel(deadline.entry.topicId).zh}</strong>（
                    {deadline.date} · 还有 {daysUntil(deadline.date, nowIso)} 天）。
                  </>
                )}
              </>
            ) : (
              <>本期暂无草案动向。</>
            )}{" "}
            {totalPain > 0 ? (
              <>
                <strong>{painTopics}</strong> 个主题有买家原话（共 {totalPain} 条）。
              </>
            ) : (
              <span className="ab-lead__hint">
                痛点未采集 — 在 Discovery &amp; Review 运行「扫描本配置全部痛点」后，行内会出现买家原话。
              </span>
            )}
          </p>

          {active.length > 0 && (
            <ol className="ab-list" data-testid="ab-list">
              {active.map((r) => renderRow(r))}
            </ol>
          )}
          {active.length === 0 && (
            <p className="ab-lead" data-testid="ab-empty">
              本期 {radar.length} 个主题均无草案/痛点信号 — 系统平静观察中。载入 WP29 真实 feed
              或扫描本配置痛点后重新分析。
            </p>
          )}

          {/* 法规存量 footer: domains with rules but no motion this period. */}
          {baseline.length > 0 && (
            <div className="ab-baseline" data-testid="ab-baseline">
              <p className="ab-baseline__lead" data-testid="ab-baseline-toggle">
                其余 {baseline.length} 个主题为现行法存量（以严格为主），本期无草案/痛点变动：
              </p>
              <ul className="ab-domains">
                {domains.map((d) => {
                  const inDomain = baseline.filter((r) => r.domainId === d);
                  const rules = inDomain.reduce((n, r) => n + (r.ruleCount ?? 0), 0);
                  const open = openDomains.has(d);
                  return (
                    <li key={d} className="ab-domain">
                      <button
                        type="button"
                        data-testid={`ab-domain-${d}`}
                        className="ab-domain__row"
                        aria-expanded={open}
                        onClick={() =>
                          setOpenDomains((prev) => {
                            const next = new Set(prev);
                            if (next.has(d)) next.delete(d);
                            else next.add(d);
                            return next;
                          })
                        }
                      >
                        {d} · {inDomain.length} 主题 · 共 {rules} 条现行规则 {open ? "▾" : "▸"}
                      </button>
                      {open && (
                        <span className="ab-domain__topics">
                          {inDomain
                            .map((r) => `${topicLabel(r.topicId).zh}（${r.ruleCount ?? 0}）`)
                            .join(" · ")}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}

      {disclaimer && (
        <p className="cross-matrix__disclaimer" data-testid="ab-disclaimer">
          {disclaimer}
        </p>
      )}
    </section>
  );
}
